/**
 * Closing a stale meeting action item, with a reason.
 *
 * WHY THIS EXISTS. Until 2026-09-17 the only per-item verbs in the product were
 * the Review Queue's approve / reject, and both needed a `pending_review`
 * ASSIGNMENT row. An item that distribution left `pending` or `escalated` has no
 * assignment, so nothing in the UI could touch it — and neither approve nor
 * reject wrote `read_ai_action_items.status` anyway, so the open counts never
 * moved. (Approve is gone entirely as of 2026-09-18; declining is what is left
 * of that pair, and it still writes no item status.) A client arrived with 79 open items of which 67 were verifiably done,
 * duplicate or superseded, and the implementation lead had no way to clear one
 * of them. (`plan/2026-09-17_FROM-NTX_close-stale-action-items.md`.)
 *
 * The close path was deliberately NOT approve, and the reason has outlived
 * approval itself: closing must create no Azure DevOps work item, which is
 * exactly what an approval used to do and what a developer's ACCEPT does now.
 * Filing a ticket for work somebody already did is the wrong outcome whichever
 * verb files it. Closing sets `status: 'closed'` plus the
 * reason/who/when/evidence, creates no work item, and works whatever the item's
 * `distributionStatus` is and whether or not it has an assignment.
 *
 * The vocabulary lives here rather than in `meetingIntelligence.types.ts`
 * because BOTH surfaces that list action items read it — Meeting Detail's
 * Action Items tab and Module 360's — and a second spelling of "wont_do" in the
 * second consumer is the bug this module exists to prevent.
 */
import type { ReadAiActionItem } from "./meetingIntelligence.types";

/**
 * The four reasons an item may be closed for. The WIRE VALUES — the API
 * validates against exactly this set, so never send a label.
 */
export type ActionItemCloseReason =
  | "done"
  | "duplicate"
  | "superseded"
  | "wont_do";

/** Every close reason, in the order a human should read them. */
export const ACTION_ITEM_CLOSE_REASONS: ReadonlyArray<{
  value: ActionItemCloseReason;
  /** What a person picks. Never sent to the API. */
  label: string;
  /** Lower-case form for a sentence ("Close 38 items as already done?"). */
  sentence: string;
  /** Why you would pick this one rather than its neighbour. */
  helper: string;
}> = [
  {
    value: "done",
    label: "Already done",
    sentence: "already done",
    helper:
      "The work exists — it shipped, or it was never needed because the behaviour was already there.",
  },
  {
    value: "duplicate",
    label: "Duplicate of another item",
    sentence: "a duplicate",
    helper:
      "The same request was extracted more than once. Name the item it duplicates so the trail survives.",
  },
  {
    value: "superseded",
    label: "Superseded",
    sentence: "superseded",
    helper:
      "A later meeting changed the decision, so this wording no longer describes what we agreed to build.",
  },
  {
    value: "wont_do",
    label: "Won't do",
    sentence: "won't do",
    helper:
      "A deliberate decision not to do it. This is the only reason that records a refusal rather than a completion.",
  },
];

const REASON_BY_VALUE = new Map(
  ACTION_ITEM_CLOSE_REASONS.map((r) => [r.value, r]),
);

/** True when `value` is one of the four the API accepts. */
export const isCloseReason = (
  value: unknown,
): value is ActionItemCloseReason =>
  typeof value === "string" && REASON_BY_VALUE.has(value as ActionItemCloseReason);

/**
 * Human label for a stored reason. An UNKNOWN value renders as itself rather
 * than disappearing — the column is free text at the API and a future reason
 * must not show as blank.
 */
export const closeReasonLabel = (value?: string | null): string => {
  if (!value) return "";
  return REASON_BY_VALUE.get(value as ActionItemCloseReason)?.label ?? value;
};

/** Sentence form of a stored reason, for the confirmation line. */
export const closeReasonSentence = (value?: string | null): string => {
  if (!value) return "";
  return (
    REASON_BY_VALUE.get(value as ActionItemCloseReason)?.sentence ?? value
  );
};

/** The close fields an item carries once somebody has closed it. */
export interface ActionItemCloseFields {
  /** `'open'` | `'closed'` — the lifecycle column the counts filter on. */
  status?: string | null;
  closedReason?: string | null;
  /** `user.id` of whoever closed it. */
  closedBy?: number | null;
  closedAt?: string | null;
  /** Free text or a URL — the evidence the closer stood on. */
  closedEvidence?: string | null;
  /** For `reason: 'duplicate'` — the item this one duplicates. */
  duplicateOfItemId?: number | null;
}

/**
 * Is this item closed?
 *
 * Reads `status`, NOT `distributionStatus` — the two are different columns with
 * different vocabularies, and `distributionStatus: 'rejected'` is a reviewer
 * saying the item was never real work, not a closure.
 *
 * An item from an API that predates the close path has no `status` at all, and
 * that reads as OPEN: absence is never evidence of closure.
 */
export const isClosedActionItem = (
  item: Pick<ActionItemCloseFields, "status">,
): boolean => item.status === "closed";

/** What the close dialog collects before it is turned into a payload. */
export interface ActionItemCloseInput {
  reason: ActionItemCloseReason;
  /** Optional free text / URL. Trimmed; blank is omitted entirely. */
  evidence?: string;
  /** Optional other-item reference, only meaningful for `duplicate`. */
  duplicateOfItemId?: number | null;
}

/** PATCH body for one item: `PATCH /read-ai-action-items/:id`. */
export interface ActionItemClosePayload {
  action: "close";
  reason: ActionItemCloseReason;
  evidence?: string;
  duplicateOfItemId?: number;
}

/** PATCH body that puts a closed item back: the close fields are cleared. */
export interface ActionItemReopenPayload {
  action: "reopen";
}

/** One item inside a bulk close. */
export interface ActionItemBulkCloseEntry {
  id: number;
  reason: ActionItemCloseReason;
  evidence?: string;
  duplicateOfItemId?: number;
}

/**
 * POST body for the bulk close: ONE MEETING PER CALL, all-or-nothing.
 * This is the path that clears a 67-item backlog; a loop of single closes would
 * leave the list half-cleared on the first refusal.
 */
export interface ActionItemBulkClosePayload {
  action: "close-bulk";
  meetingId: number;
  items: ActionItemBulkCloseEntry[];
}

/** One per-item outcome the bulk close reports back. */
export interface ActionItemBulkCloseOutcome {
  id: number;
  status?: string | null;
  ok?: boolean;
  error?: string | null;
}

/** What the bulk close resolves to. Every field optional — see `closedCount`. */
export interface ActionItemBulkCloseReply {
  closed?: number;
  items?: ActionItemBulkCloseOutcome[];
}

/**
 * Build the single-item PATCH body.
 *
 * Blank evidence is OMITTED rather than sent as `""` — an empty string is a
 * claim that evidence was given, and the record should not carry one. The
 * duplicate reference is dropped for every reason except `duplicate`, so
 * changing your mind in the dialog cannot leave a stale id on a `done` close.
 */
export const buildClosePayload = (
  input: ActionItemCloseInput,
): ActionItemClosePayload => {
  const payload: ActionItemClosePayload = {
    action: "close",
    reason: input.reason,
  };
  const evidence = input.evidence?.trim();
  if (evidence) payload.evidence = evidence;
  if (
    input.reason === "duplicate" &&
    input.duplicateOfItemId != null &&
    Number.isFinite(input.duplicateOfItemId)
  ) {
    payload.duplicateOfItemId = input.duplicateOfItemId;
  }
  return payload;
};

/**
 * Build the bulk POST body — one reason applied to every selected item of ONE
 * meeting. `duplicateOfItemId` is deliberately not carried into a bulk close
 * even when the reason is `duplicate`: thirty-eight items cannot all duplicate
 * the same one item, and pretending otherwise would write a false trail.
 */
export const buildBulkClosePayload = (
  meetingId: number,
  ids: number[],
  input: ActionItemCloseInput,
): ActionItemBulkClosePayload => {
  const evidence = input.evidence?.trim();
  return {
    action: "close-bulk",
    meetingId,
    items: ids.map((id) => ({
      id,
      reason: input.reason,
      ...(evidence ? { evidence } : {}),
    })),
  };
};

/** The reopen PATCH body. */
export const buildReopenPayload = (): ActionItemReopenPayload => ({
  action: "reopen",
});

/**
 * How many items a bulk reply actually closed.
 *
 * Prefers the server's own `closed` count, falls back to counting the per-item
 * outcomes that did not report a failure, and finally to the number asked for —
 * in that order, because an API that predates one of these fields must not be
 * reported as having closed nothing.
 */
export const closedCount = (
  reply: ActionItemBulkCloseReply | null | undefined,
  requested: number,
): number => {
  if (typeof reply?.closed === "number") return reply.closed;
  if (Array.isArray(reply?.items)) {
    return reply.items.filter((o) => o.ok !== false && !o.error).length;
  }
  return requested;
};

/**
 * The confirmation sentence the dialog asks before a bulk close, e.g.
 * "Close 38 items as already done?". A count in the question is the whole
 * point: the operator is about to change 38 rows and the only honest way to
 * say so is to say so.
 */
export const closeConfirmation = (
  count: number,
  reason: ActionItemCloseReason,
): string =>
  `Close ${count} item${count === 1 ? "" : "s"} as ${closeReasonSentence(reason)}?`;

/**
 * One line describing a closure, for a row that is already closed:
 * "Already done · Alice Smith · 17 Sep 2026".
 *
 * Every part is optional — an older API returns the status and nothing else —
 * and a missing part is simply left out rather than rendered as "unknown".
 */
export const closedSummary = (
  item: ActionItemCloseFields,
  closerLabel?: string | null,
): string => {
  const parts: string[] = [];
  const reason = closeReasonLabel(item.closedReason);
  if (reason) parts.push(reason);
  if (closerLabel) parts.push(closerLabel);
  if (item.closedAt) {
    const when = new Date(item.closedAt);
    parts.push(
      Number.isNaN(when.getTime())
        ? String(item.closedAt)
        : when.toLocaleDateString(),
    );
  }
  return parts.join(" · ");
};

/** Short label for an item in the duplicate picker. */
export const actionItemOptionLabel = (
  item: Pick<ReadAiActionItem, "id" | "text">,
): string => {
  const text = (item.text || "").trim();
  const head = text.length > 70 ? `${text.slice(0, 70)}…` : text;
  return head ? `#${item.id} — ${head}` : `#${item.id}`;
};

/**
 * How many of a meeting's action items are still OPEN — i.e. still work.
 *
 * `actionItemCount` is every item the meeting ever produced and deliberately
 * does not shrink when a human closes one: it is the record of what was
 * extracted. Any badge that means *outstanding work* must read
 * `openActionItemCount` instead — a meeting list saying "23 action items" about
 * a meeting whose 23 items were all closed as already-done is the complaint
 * this whole path came from.
 *
 * FALLS BACK TO THE TOTAL, never to zero. An API that predates the close path
 * omits the field, and on that API no item can be closed, so the total IS the
 * open count. Reading absence as 0 would empty every badge against an older
 * server.
 */
export function openActionItems(meeting: {
  openActionItemCount?: number | null;
  actionItemCount?: number | null;
}): number {
  const open = meeting.openActionItemCount;
  if (typeof open === "number") return open;
  return meeting.actionItemCount ?? 0;
}
